import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Save, Trash2 } from 'lucide-react';
import type { AssetCategoryDTO } from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useCategories } from '../../api/hooks';
import { toast } from '../../components/Toasts';
import { TableScroll } from '../../components/TableScroll';
import { queryKeys } from '../../api/queryKeys';

/** Asset classes: name, colour and order. Shown as a view of the Metadata model page (`embedded`, 0.91.0). */
export function CategoriesAdmin({ embedded = false }: { embedded?: boolean }) {
  const q = useCategories();
  const qc = useQueryClient();
  const [edits, setEdits] = useState<Record<string, { name: string; color: string }>>({});
  const [draft, setDraft] = useState({ name: '', color: '#8e7dff' });
  const items = q.data?.items ?? [];
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: queryKeys.categories() });
      await qc.invalidateQueries({ queryKey: queryKeys.assets() });
      toast(ok);
      return true;
    } catch (err) {
      toast(errorMessage(err), 'error');
      return false;
    }
  };
  const save = (c: AssetCategoryDTO, patch: Partial<{ name: string; color: string; position: number }>) =>
    run(() => api(`/api/v1/asset-categories/${c.id}`, { method: 'PUT', json: { name: c.name, color: c.color, position: c.position, ...patch } }), 'Saved');
  const swap = async (i: number, j: number) => {
    const a = items[i]!;
    const b = items[j]!;
    await save(a, { position: b.position === a.position ? b.position + (j > i ? 1 : -1) : b.position });
    await save(b, { position: a.position });
  };

  return (
    <div className={embedded ? 'classes-embedded' : 'page'} data-testid="classes-admin">
      {!embedded && <h2>Asset classes</h2>}
      <p className="lede">What kind of thing an asset is, such as a programme, a clip, a trailer or raw footage. Every asset has at most one class. It shows on library cards and filters the library. Watch folders can set it for what they ingest; afterwards only administrators and media managers change it.</p>
      <section className="panel-card" style={{ maxWidth: 760 }} data-testid="categories">
        <TableScroll>
        <table className="table compact">
          <thead><tr><th>Colour</th><th>Name</th><th>Assets</th><th /></tr></thead>
          <tbody>
            {items.map((c, i) => {
              const e = edits[c.id] ?? { name: c.name, color: c.color };
              const dirty = e.name !== c.name || e.color !== c.color;
              return (
                <tr key={c.id} data-testid="category-row">
                  <td><input type="color" value={e.color} aria-label={`Colour of ${c.name}`} onChange={(ev) => setEdits({ ...edits, [c.id]: { ...e, color: ev.target.value } })} /></td>
                  <td><input className="input" value={e.name} onChange={(ev) => setEdits({ ...edits, [c.id]: { ...e, name: ev.target.value } })} aria-label="Name" /></td>
                  <td className="muted">{c.assets}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {dirty && <button className="btn primary sm" onClick={() => void save(c, e).then((ok) => ok && setEdits((x) => { const n = { ...x }; delete n[c.id]; return n; }))}><Save size={12} /> Save</button>}
                    <button className="btn ghost icon sm" disabled={i === 0} aria-label="Move up" onClick={() => void swap(i, i - 1)}><ArrowUp size={12} /></button>
                    <button className="btn ghost icon sm" disabled={i === items.length - 1} aria-label="Move down" onClick={() => void swap(i, i + 1)}><ArrowDown size={12} /></button>
                    <button className="btn ghost icon sm danger" aria-label={`Remove ${c.name}`} onClick={() => {
                      if (!window.confirm(`Remove “${c.name}”?${c.assets ? ` Its ${c.assets} asset${c.assets === 1 ? '' : 's'} lose their class.` : ''} Watch folders that set it stop setting a category.`)) return;
                      void run(() => api(`/api/v1/asset-categories/${c.id}`, { method: 'DELETE' }), `Removed ${c.name}`);
                    }}><Trash2 size={12} /></button>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td><input type="color" value={draft.color} aria-label="Colour of the new class" onChange={(e) => setDraft({ ...draft, color: e.target.value })} /></td>
              <td><input className="input" placeholder="New class, e.g. Promo" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="new-category" /></td>
              <td />
              <td><button className="btn sm" disabled={!draft.name.trim()} onClick={() => void run(() => api('/api/v1/asset-categories', { method: 'POST', json: draft }), `Added ${draft.name}`).then((ok) => ok && setDraft({ name: '', color: '#8e7dff' }))}><Plus size={12} /> Add</button></td>
            </tr>
          </tbody>
        </table>
        </TableScroll>
      </section>
    </div>
  );
}
